import { IHttpService } from '../iHttpService';
import { ApiResponse } from '@/types/response';
import axios from 'axios';
import { ApiRequest } from '@/types/request';
import { ObjectFactory } from '@/config/objectFactory';
import { MedTransGoConstants } from '@/util/constants';
import { ToastEvent } from '@/types/event';
import { CommonUtils } from '@/util/commonUtils';
import { EventBus } from '../../main';

/**
 * @author <Aniket.P>
 * @description Axios HTTP service implementation
 * @copyright Supra International, inc
 */
export class AxiosHttpService implements IHttpService {

    private specialErrors: any = {
        'Request failed with status code 404': MedTransGoConstants.ERROR_CODE_ROUTE_NOT_FOUND,
        'Network Error': MedTransGoConstants.ERROR_CODE_NETWORK_ERROR,
    };

    public async post<T>(request: ApiRequest): Promise<ApiResponse<T>> {
        request.method = 'POST';
        return this.executeHttpCall<T>(request);
    }

    public async get<T = any>(request: ApiRequest): Promise<ApiResponse<T>> {
        request.method = 'GET';
        return this.executeHttpCall<T>(request);
    }

    public async put<T>(request: ApiRequest): Promise<ApiResponse<T>> {
        request.method = 'PUT';
        return this.executeHttpCall<T>(request);
    }

    public async delete<T>(request: ApiRequest): Promise<ApiResponse<T>> {
        request.method = 'DELETE';
        return this.executeHttpCall<T>(request);
    }

    private async executeHttpCall<T = any>(request: ApiRequest) {
        let apiResponse: ApiResponse<T> = {
            success: false,
        };
        let response = null;
        const loaderEvent = request.loaderEvent;
        try {
            if (!request.timeout) {
                request.timeout = 500000;
            }
            if (loaderEvent) {
                loaderEvent.message = loaderEvent.message ? loaderEvent.message : 'Please wait';
                loaderEvent.show = true;
                EventBus.$emit(MedTransGoConstants.EVENT_SHOW_LOADER, loaderEvent);
            }
            this.addAuthoriztionToken(request);
            response = await axios.request(request);
            if (request.responseType && request.responseType === 'arraybuffer') {
                return response.data;
            }
            apiResponse.success = true;
            apiResponse.totalCount = response.data.totalCount;
        } catch (error) {
            if (error.message && this.specialErrors[error.message]) {
                apiResponse.errorCode = MedTransGoConstants.ERROR_CODE_NETWORK_ERROR;
                const toastEvent: ToastEvent = {
                    message: CommonUtils.translateErrorCode(this.specialErrors[error.message]),
                    title: 'Network Error',
                    timeout: 5000,
                    varient: 'danger',
                };
                EventBus.$emit(MedTransGoConstants.EVENT_SHOW_TOAST, toastEvent);
                throw error;
            } else {
                response = error.response;
            }
        } finally {
            if (response && !request.responseType) {
                if (typeof response.data === 'string') {
                    apiResponse.data = response.data;
                } else {
                    apiResponse = response.data;
                }
                apiResponse.headers = response.headers;
                apiResponse.statusCode = response.status;
                apiResponse.statusText = response.statusText;
            } else if (!apiResponse.errorCode) {
                apiResponse.errorCode = 'UNAUTHORIZED_REQUEST';
            }
            if (loaderEvent) {
                loaderEvent.show = false;
                EventBus.$emit(MedTransGoConstants.EVENT_SHOW_LOADER, loaderEvent);
            }
        }
        return apiResponse;
    }


    private addAuthoriztionToken(request: ApiRequest) {
        const idToken = ObjectFactory.getCacheService().getValue(MedTransGoConstants.FIELD_NAME_TOKEN);
        if (idToken) {
            if (!request.headers) {
                request.headers = {};
            }
            request.headers.Authorization = `Bearer ${idToken}`;
        }
    }
}
